import React from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'
import { useTranslation } from 'react-i18next'
import { LazyLoadImage } from 'react-lazy-load-image-component'

import { CheckIcon, PanelCloseIcon, PanelOpenIcon } from 'images'
import { formatMediaDimensions, getYoutubeThumbnailUrl } from 'domain/adPreview/format'

// A non-YouTube video's `url` points at the video file itself (e.g. `.mp4`),
// not an image — feeding it to LazyLoadImage's <img> renders a broken-image
// icon. Mirrors the `renderMedia` pattern in AdCards/CarouselMedia.jsx: a
// real YouTube item gets its public thumbnail image, any other video gets a
// muted/looping <video> in place of the thumbnail, everything else an image.
const renderThumb = (media, className) => {
  if (media?.youtube_video_id) {
    return (
      <LazyLoadImage
        className={className}
        src={getYoutubeThumbnailUrl(media.youtube_video_id)}
        alt={media?.filename}
        visibleByDefault
      />
    )
  }
  if (media?.type?.includes('video')) {
    return <video className={className} src={media?.url} autoPlay muted loop playsInline />
  }
  return (
    <LazyLoadImage className={className} src={media?.url} alt={media?.filename} visibleByDefault />
  )
}

const ImageSidebar = ({ creative, expanded, onToggleExpanded, selectedMediaId, onSelectMedia }) => {
  const { t } = useTranslation()
  const isCarousel = Array.isArray(creative?.carousel_cards) && creative.carousel_cards.length > 0

  return (
    <div className={cn('preview-ads-sidebar', { 'preview-ads-sidebar--collapsed': !expanded })}>
      <div className="preview-ads-sidebar__header ">
        <span className="preview-ads-sidebar__header-label">{t('Recommendations.adPreview.images')}</span>
        <button type="button" className="preview-ads-sidebar__toggle" onClick={onToggleExpanded}>
          {expanded ? <PanelCloseIcon width={16} /> : <PanelOpenIcon width={16} />}
        </button>
      </div>
      <div className="preview-ads-sidebar__scroll">
        {isCarousel ? (
          <div className="preview-ads-image-card">
            {creative.carousel_cards[0]?.media?.url &&
              renderThumb(creative.carousel_cards[0].media, 'preview-ads-image-card__thumb')}
            <div className="preview-ads-image-card__meta">
              <div className="preview-ads-image-card__filename">
                {t('Recommendations.adPreview.carouselAd')}
              </div>
              <div className="preview-ads-image-card__dims">
                {t('Recommendations.adPreview.carouselAdCards', {
                  count: creative.carousel_cards.length,
                })}
              </div>
            </div>
          </div>
        ) : (
          creative?.media?.map((media) => (
            <button
              key={media.id ?? media.url}
              type="button"
              className={cn('preview-ads-image-card', { 'preview-ads-image-card--selected': media.id === selectedMediaId })}
              onClick={() => onSelectMedia(media.id === selectedMediaId ? undefined : media.id)}
            >
              {renderThumb(media, 'preview-ads-image-card__thumb')}
              {media.id === selectedMediaId && (
                <span className="preview-ads-image-card__check">
                  <CheckIcon width={12} />
                </span>
              )}
              <div className="preview-ads-image-card__meta">
                <div className="preview-ads-image-card__filename">{media.filename}</div>
                <div className="preview-ads-image-card__dims">{formatMediaDimensions(media)}</div>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  )
}

ImageSidebar.propTypes = {
  creative: PropTypes.shape({
    media: PropTypes.array,
    carousel_cards: PropTypes.array,
  }),
  expanded: PropTypes.bool.isRequired,
  onToggleExpanded: PropTypes.func.isRequired,
  selectedMediaId: PropTypes.string,
  onSelectMedia: PropTypes.func.isRequired,
}

export default ImageSidebar
